import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Layout from '../../components/admin/Layout';
import { Button, Card, EmptyState, Spinner, StatusBadge, formatDate } from '../../components/admin/ui';
import adminApi, { readableError } from '../../utils/adminApi';

const STATUSES = ['draft', 'active', 'closed', 'archived'];
const TYPES = ['full-time', 'part-time', 'contract', 'internship'];

const EMPTY_FORM = {
  title: '', company: '', location: '', type: 'full-time', category: '',
  description: '', requirements: '', salaryMin: '', salaryMax: '', status: 'draft'
};

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({ totalJobs: 0, activeJobs: 0, applicationsReceived: 0 });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('all');

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const params = useMemo(() => ({
    ...(status !== 'all' ? { status } : {}),
    ...(category !== 'all' ? { category } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {})
  }), [status, category, debouncedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await adminApi.get('/admin/jobs', { params });
      setJobs(data?.data?.jobs || []);
      setStats(data?.data?.stats || { totalJobs: 0, activeJobs: 0, applicationsReceived: 0 });
    } catch (err) {
      setError(readableError(err, 'Could not load jobs.'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    adminApi.get('/admin/jobs/categories')
      .then(({ data }) => setCategories(data?.data || []))
      .catch(() => setCategories([]));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (job) => {
    setEditing(job);
    setForm({
      title: job.title || '',
      company: job.company || '',
      location: job.location || '',
      type: job.type || 'full-time',
      category: job.category || '',
      description: job.description || '',
      requirements: (job.requirements || []).join('\n'),
      salaryMin: job.salary?.min ?? '',
      salaryMax: job.salary?.max ?? '',
      status: job.status || 'draft'
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      requirements: form.requirements.split('\n').map((r) => r.trim()).filter(Boolean),
      salaryMin: form.salaryMin === '' ? null : Number(form.salaryMin),
      salaryMax: form.salaryMax === '' ? null : Number(form.salaryMax),
      category: form.category || undefined
    };
    try {
      if (editing) {
        await adminApi.put(`/admin/jobs/${editing.id}`, payload);
        toast.success('Job updated');
      } else {
        await adminApi.post('/admin/jobs', payload);
        toast.success('Job created');
      }
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(readableError(err, 'Could not save the job.'));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (job) => {
    try {
      await adminApi.patch(`/admin/jobs/${job.id}/toggle-status`);
      await load();
    } catch (err) {
      toast.error(readableError(err, 'Could not change status.'));
    }
  };

  const duplicate = async (job) => {
    try {
      await adminApi.post('/admin/jobs', {
        title: `${job.title} (Copy)`,
        company: job.company, location: job.location, type: job.type,
        category: job.category || undefined, description: job.description,
        requirements: job.requirements || [],
        salaryMin: job.salary?.min ?? null, salaryMax: job.salary?.max ?? null,
        status: 'draft'
      });
      toast.success('Job duplicated as a draft');
      await load();
    } catch (err) {
      toast.error(readableError(err, 'Could not duplicate the job.'));
    }
  };

  const remove = async (job) => {
    if (!window.confirm(`Delete “${job.title}”? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/jobs/${job.id}`);
      toast.success('Job deleted');
      await load();
    } catch (err) {
      toast.error(readableError(err, 'Could not delete the job.'));
    }
  };

  const salaryLabel = (job) => {
    const { min, max } = job.salary || {};
    if (!min && !max) return '—';
    const fmt = (n) => `₹${Number(n).toLocaleString('en-IN')}`;
    if (min && max) return `${fmt(min)} – ${fmt(max)}`;
    return fmt(min || max);
  };

  const input = 'w-full rounded-xl border-0 bg-slate-50 p-3 text-sm text-slate-900 ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-indigo-500';
  const label = 'mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500';

  return (
    <Layout>
      <div className="space-y-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-xl">💼</div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">Jobs</h1>
              <p className="text-sm text-slate-500">Postings shown on the public jobs board</p>
            </div>
          </div>
          <Button variant="primary" onClick={openCreate}>+ New job</Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[
            { label: 'Total jobs', value: stats.totalJobs, icon: '💼' },
            { label: 'Active', value: stats.activeJobs, icon: '✅' },
            { label: 'Applications', value: stats.applicationsReceived, icon: '📥' }
          ].map((s) => (
            <Card key={s.label} className="flex items-center justify-between p-5">
              <div>
                <p className="text-sm font-medium text-slate-500">{s.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{s.value}</p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-lg">{s.icon}</div>
            </Card>
          ))}
        </div>

        <Card className="overflow-hidden">
          {/* Filters */}
          <div className="flex flex-col gap-3 border-b border-slate-100 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-1.5">
              {['all', ...STATUSES].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium capitalize transition ${
                    status === s ? 'bg-slate-900 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                className="rounded-xl border-0 bg-slate-50 py-2 pl-3 pr-8 text-sm text-slate-700 ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-indigo-500">
                <option value="all">All categories</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input
                type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search title, company, location…"
                className="w-full rounded-xl border-0 bg-slate-50 px-3 py-2 text-sm text-slate-900 ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-indigo-500 lg:w-64"
              />
            </div>
          </div>

          {error ? (
            <EmptyState icon="⚠️" title="Could not load jobs" description={error}
              action={<Button variant="primary" onClick={load}>Try again</Button>} />
          ) : loading ? (
            <div className="flex items-center justify-center gap-3 py-20 text-sm text-slate-500">
              <Spinner /> Loading…
            </div>
          ) : jobs.length === 0 ? (
            <EmptyState
              title={search || status !== 'all' || category !== 'all' ? 'No matching jobs' : 'No jobs posted yet'}
              description={search || status !== 'all' || category !== 'all'
                ? 'Try a different search or clear the filters.'
                : 'Create your first job posting to show it on the jobs board.'}
              action={<Button variant="primary" onClick={openCreate}>+ New job</Button>}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50/80">
                  <tr>
                    {['Role', 'Category', 'Salary', 'Status', 'Views', 'Posted', ''].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {jobs.map((job) => (
                    <tr key={job.id} className="transition hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{job.title}</p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {[job.company, job.location, job.type].filter(Boolean).join(' · ')}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {job.category ? (
                          <span className="inline-flex rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                            {job.category}
                          </span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">{salaryLabel(job)}</td>
                      <td className="px-4 py-3">
                        <button type="button" onClick={() => toggleStatus(job)} title="Click to advance status">
                          <StatusBadge status={job.status} />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">{job.views ?? 0}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500">{formatDate(job.createdAt)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => openEdit(job)}>Edit</Button>
                          <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => duplicate(job)}>Duplicate</Button>
                          <Button variant="ghost" className="px-2 py-1 text-xs text-rose-600 hover:bg-rose-50" onClick={() => remove(job)}>Delete</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Create / edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setShowForm(false)} />
          <form
            onSubmit={submit}
            className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
              <h2 className="text-lg font-semibold text-slate-900">
                {editing ? 'Edit job' : 'New job'}
              </h2>
              <button type="button" onClick={() => setShowForm(false)} aria-label="Close"
                className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="max-h-[65vh] space-y-4 overflow-y-auto px-6 py-5">
              <div>
                <label className={label} htmlFor="job-title">Job title *</label>
                <input id="job-title" required className={input} value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. CNC Operator" />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={label} htmlFor="job-company">Company</label>
                  <input id="job-company" className={input} value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })} />
                </div>
                <div>
                  <label className={label} htmlFor="job-location">Location</label>
                  <input id="job-location" className={input} value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })} />
                </div>
                <div>
                  <label className={label} htmlFor="job-type">Type</label>
                  <select id="job-type" className={input} value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    {TYPES.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label} htmlFor="job-category">Category</label>
                  <select id="job-category" className={input} value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    <option value="">Select category</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label} htmlFor="job-min">Salary min (₹/year)</label>
                  <input id="job-min" type="number" min="0" className={input} value={form.salaryMin}
                    onChange={(e) => setForm({ ...form, salaryMin: e.target.value })} />
                </div>
                <div>
                  <label className={label} htmlFor="job-max">Salary max (₹/year)</label>
                  <input id="job-max" type="number" min="0" className={input} value={form.salaryMax}
                    onChange={(e) => setForm({ ...form, salaryMax: e.target.value })} />
                </div>
              </div>

              <div>
                <label className={label} htmlFor="job-desc">Description *</label>
                <textarea id="job-desc" required rows={4} className={input} value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>

              <div>
                <label className={label} htmlFor="job-req">Requirements (one per line)</label>
                <textarea id="job-req" rows={4} className={input} value={form.requirements}
                  onChange={(e) => setForm({ ...form, requirements: e.target.value })}
                  placeholder={'ITI qualified\n2+ years experience'} />
              </div>

              <div>
                <label className={label} htmlFor="job-status">Status</label>
                <select id="job-status" className={input} value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                </select>
                <p className="mt-1.5 text-xs text-slate-400">Only “active” jobs appear on the public site.</p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-4">
              <Button onClick={() => setShowForm(false)}>Cancel</Button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Create job'}
              </button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
};

export default Jobs;
